import Accordion from "@/components/Accordion";
import singleOptInMobile from "@/assets/images/collect/single-opt-in-mobile.png";
import doubleOptInMobile from "@/assets/images/collect/double-opt-in-mobile.png";
import shopifyLogo from "@/assets/images/collect/shopify-logo-dark.png";
import klaviyoLogo from "@/assets/images/collect/klaviyo-logo-dark.png";
import { useTranslation } from "react-i18next";
import { P, Small } from "@/components/Typography";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import {
  faCheckSquare,
  faXmarkSquare,
} from "@fortawesome/pro-duotone-svg-icons";
import { Button } from "@/components/Button";
import { PhoneNumbersTable } from "@/components/Table";

interface IProps {
  healthChecksHook: IGetHealthChecks;
}

const HealthCheck = ({ healthChecksHook }: IProps) => {
  const { t } = useTranslation();

  const { data: healthChecks, isPending: isPendingHealthChecks } =
    healthChecksHook;

  const renderSectionHeader = (title: string) =>
    !!title && (
      <div className="mt-10 md:mt-30">
        <Small color="primary" className="font-semibold">
          {title.toUpperCase()}
        </Small>
      </div>
    );

  const renderSectionDescription = (description?: string) => (
    <P className="whitespace-pre-wrap break-all">{description}</P>
  );

  const renderSuccessOrErrorIcon = (status: boolean) => {
    if (status) {
      return (
        <FontAwesomeIcon
          data-testid="check-success-icon"
          icon={faCheckSquare}
          className="text-green"
        />
      );
    } else {
      return (
        <FontAwesomeIcon
          data-testid="check-fail-icon"
          icon={faXmarkSquare}
          className="text-red"
        />
      );
    }
  };

  const renderPhoneNumbersTable = () => {
    if (!healthChecks?.secondOptIn?.phoneNumbers) return null;
    const { phoneNumbers } = healthChecks.secondOptIn;

    return (
      <PhoneNumbersTable
        isLoading={isPendingHealthChecks}
        phoneNumbers={phoneNumbers}
      />
    );
  };

  const renderCheckAndStatus = (
    checkType: "AVAILABILITY" | "WORDING_COMPLIANCE" | "DOUBLE_OPT_IN",
    status?: IHealthCheckStatus | boolean,
  ) => {
    const getCheckTitle = () => {
      switch (checkType) {
        case "AVAILABILITY":
          return t("sms.healthCheck.availabilityCheck");
        case "WORDING_COMPLIANCE":
          return t("sms.healthCheck.wordingComplianceCheck");
        default:
          return t("sms.healthCheck.doubleOptInCheck");
      }
    };

    if (typeof status === "boolean") {
      return (
        <div className="flex flex-row items-center gap-10 font-bold">
          {getCheckTitle()}
          {renderSuccessOrErrorIcon(status)}
        </div>
      );
    }

    return (
      <div className="flex flex-row items-center gap-10 font-bold">
        {getCheckTitle()}
        {renderSuccessOrErrorIcon(status === "PASS")}
      </div>
    );
  };

  const renderTopContent = (optInType: "SINGLE_OPT_IN" | "DOUBLE_OPT_IN") => (
    <div className="flex flex-row gap-10 font-semibold">
      <img
        className="max-h-30"
        src={optInType === "SINGLE_OPT_IN" ? shopifyLogo : klaviyoLogo}
      />
      {optInType === "SINGLE_OPT_IN"
        ? t("sms.healthCheck.singleOptIn.topContent").toUpperCase()
        : t("sms.healthCheck.doubleOptIn.topContent").toUpperCase()}
    </div>
  );

  const renderSingleOptInTextSection = () => {
    if (!healthChecks?.firstOptIn) return null;

    const { firstOptIn } = healthChecks;

    return (
      <div className="flex flex-grow flex-col">
        {renderSectionHeader(t("sms.healthCheck.singleOptIn.optInTitle"))}

        <div className="mb-20">
          {renderSectionDescription(firstOptIn?.content)}
        </div>

        {renderCheckAndStatus("WORDING_COMPLIANCE", firstOptIn?.contentStatus)}

        {renderSectionHeader(
          t("sms.healthCheck.singleOptIn.privacyPolicy.title"),
        )}

        <div className="mb-20 mt-2">
          <Button
            as="a"
            target="_blank"
            variant="link"
            icon={faCaretRight}
            iconClassName="h-15 w-15"
            iconPosition="end"
            href={firstOptIn?.privacyPolicy?.url}>
            {t("sms.healthCheck.singleOptIn.privacyPolicy.linkTitle")}
          </Button>
        </div>

        <div className="mb-20">
          {renderCheckAndStatus(
            "AVAILABILITY",
            !!firstOptIn?.privacyPolicy?.url,
          )}
        </div>

        {renderCheckAndStatus(
          "WORDING_COMPLIANCE",
          firstOptIn?.privacyPolicy?.status,
        )}

        {renderSectionHeader(
          t("sms.healthCheck.singleOptIn.termsOfService.title"),
        )}

        <div className="mb-20 mt-2">
          <Button
            as="a"
            variant="link"
            href={firstOptIn?.termsOfService?.url}
            iconClassName="h-15 w-15"
            target="_blank"
            icon={faCaretRight}
            iconPosition="end">
            {t("sms.healthCheck.singleOptIn.termsOfService.linkTitle")}
          </Button>
        </div>

        <div className="mb-20">
          {renderCheckAndStatus(
            "AVAILABILITY",
            !!firstOptIn?.termsOfService?.url,
          )}
        </div>

        {renderCheckAndStatus(
          "WORDING_COMPLIANCE",
          firstOptIn?.termsOfService?.status,
        )}
      </div>
    );
  };

  const renderDoubleOptInTextSection = () => {
    if (!healthChecks?.firstOptIn) return null;

    const { secondOptIn } = healthChecks;

    return (
      <div className="flex flex-grow flex-col">
        {renderSectionHeader(t("sms.healthCheck.doubleOptIn.requestSmsTitle"))}

        <div className="mb-20">
          {renderSectionDescription(secondOptIn?.content)}
        </div>

        {renderCheckAndStatus("WORDING_COMPLIANCE", secondOptIn?.contentStatus)}

        {renderSectionHeader(t("sms.healthCheck.doubleOptIn.smsList.title"))}

        <div className="mb-20 mt-2">
          <Button
            as="a"
            href={`https://www.klaviyo.com/list/${secondOptIn?.smsList?.listId}`}
            target="_blank"
            variant="link"
            icon={faCaretRight}
            iconClassName="h-15 w-15"
            iconPosition="end">
            {t("sms.healthCheck.doubleOptIn.smsList.subscribersLink", {
              listId: secondOptIn?.smsList?.listId,
            })}
          </Button>
        </div>

        {renderCheckAndStatus(
          "DOUBLE_OPT_IN",
          secondOptIn?.smsList.doubleOptInStatus,
        )}

        {renderSectionHeader(
          t("sms.healthCheck.doubleOptIn.phoneNumbers.title"),
        )}

        <div className="mt-10">{renderPhoneNumbersTable()}</div>
      </div>
    );
  };

  const renderSingleOptInAccordion = () => (
    <Accordion
      defaultIsExpanded
      hideToggle
      topContent={renderTopContent("SINGLE_OPT_IN")}>
      <div className="flex flex-grow flex-col items-center gap-20 p-10 md:flex-row md:gap-[95px] md:p-30">
        <img
          className="h-400 mx-auto md:ml-90 md:h-[650px]"
          src={singleOptInMobile}
        />
        {renderSingleOptInTextSection()}
      </div>
    </Accordion>
  );

  const renderDoubleOptInAccordion = () => (
    <div className="mt-20">
      <Accordion
        defaultIsExpanded
        hideToggle
        topContent={renderTopContent("DOUBLE_OPT_IN")}>
        <div className="flex flex-grow flex-col items-center gap-20 p-10 md:flex-row md:gap-[95px] md:p-30">
          <img
            className="h-400 mx-auto md:ml-90 md:h-[650px]"
            src={doubleOptInMobile}
          />
          {renderDoubleOptInTextSection()}
        </div>
      </Accordion>
    </div>
  );

  return (
    <>
      {renderSingleOptInAccordion()}
      {renderDoubleOptInAccordion()}
    </>
  );
};

export default HealthCheck;
